/** EmployeeProfileDialog — unified "Employee 360" popup accessible from anywhere.
 *
 * Renders a large Dialog with sub-tabs: Overview · Attendance · Leaves · WFH ·
 * Tasks · Goals · Points · AI. Fetches `/employees/{uid}/profile-360` and
 * respects permission flags returned by the API.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RemarksPanel } from "@/pages/app/NowDashboard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import MultiSelect from "@/components/ui/multi-select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Mail, Phone, Briefcase, CalendarClock, User2, ExternalLink,
  Sparkles, Loader2, ListChecks, Trophy, Target, MapPin,
  AlertTriangle, Monitor, Clock, Home, Plane,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

function fmt(iso) { return iso ? iso.slice(0, 10) : "—"; }
import { fmtWorkspaceTime } from "@/lib/wsTime";

// Feb 2026 R100 · Personal-timezone-aware time formatter. When the
// employee has a `timezone` set on their record, all clock-in / out
// values render in that tz; otherwise falls back to the workspace tz.
function fmtTime(iso, tz) { return iso ? (fmtWorkspaceTime(iso, tz) || "—") : "—"; }
function tenureLabel(days) {
  if (!days || days < 0) return "—";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  const y = Math.floor(days / 365);
  const m = Math.floor((days - y * 365) / 30);
  return m ? `${y}y ${m}mo` : `${y}y`;
}

export default function EmployeeProfileDialog({ userId, open, onOpenChange }) {
  const { activeWorkspaceId: wsId } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("overview");
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (!open || !userId || !wsId) return;
    setBusy(true); setErr(null); setData(null); setTab("overview");
    api.get(`/workspaces/${wsId}/employees/${userId}/profile-360`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiErrorMessage(e, "Failed to load profile")))
      .finally(() => setBusy(false));
  }, [open, userId, wsId]);

  const id = data?.identity;
  const perm = data?.permissions;

  const initials = useMemo(() =>
    (id?.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase(),
    [id?.name]);

  const generateAI = async () => {
    setAiBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/members/${userId}/ai-summary`);
      const { data: d } = await api.get(`/workspaces/${wsId}/employees/${userId}/profile-360`);
      setData(d);
      toast.success("AI summary generated");
    } catch (e) { toast.error(apiErrorMessage(e, "AI unavailable")); }
    finally { setAiBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden p-0 rounded-none flex flex-col" data-testid="emp-profile-dialog">
        <DialogHeader className="sr-only">
          <DialogTitle>Employee profile</DialogTitle>
          <DialogDescription>Employee 360 popup with attendance, leaves, WFH, tasks, goals, points, and AI insights.</DialogDescription>
        </DialogHeader>
        {busy && (
          <div className="flex-1 flex items-center justify-center py-16 text-sm text-muted-foreground" data-testid="emp-profile-loading">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading profile…
          </div>
        )}
        {err && !busy && (
          <div className="p-8 text-sm" data-testid="emp-profile-error">{err}</div>
        )}
        {data && !busy && (
          <>
            <ProfileHeader id={id} initials={initials} />
            <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
              <TabsList className="rounded-none flex-wrap h-auto border-b bg-muted/20 px-4 justify-start" data-testid="emp-profile-tabs">
                {[
                  ["overview", "Overview"],
                  ["attendance", "Attendance"],
                  ["leaves", "Leaves"],
                  ["wfh", "WFH"],
                  ["tasks", "Tasks"],
                  ["goals", "Goals"],
                  ["points", "Points"],
                  ["timesheet", "Timesheet"],
                  ["remarks", "Remarks"],
                  ...(perm?.can_view_perf ? [["ai", "AI insights"]] : []),
                ].map(([v, l]) => (
                  <TabsTrigger key={v} value={v} className="rounded-none data-[state=active]:bg-background" data-testid={`emp-profile-tab-${v}`}>{l}</TabsTrigger>
                ))}
              </TabsList>
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
                <TabsContent value="overview" className="m-0 space-y-4"><OverviewTab data={data} /></TabsContent>
                <TabsContent value="attendance" className="m-0 space-y-4"><AttendanceTab data={data} /></TabsContent>
                <TabsContent value="leaves" className="m-0 space-y-4"><LeavesTab data={data} /></TabsContent>
                <TabsContent value="wfh" className="m-0 space-y-4"><WfhTab data={data} /></TabsContent>
                <TabsContent value="tasks" className="m-0 space-y-4"><TasksTab data={data} /></TabsContent>
                <TabsContent value="goals" className="m-0 space-y-4"><GoalsTab data={data} /></TabsContent>
                <TabsContent value="points" className="m-0 space-y-4"><PointsTab data={data} /></TabsContent>
                <TabsContent value="timesheet" className="m-0"><TimesheetTab userId={userId} wsId={wsId} /></TabsContent>
                <TabsContent value="remarks" className="m-0 space-y-4">
                  <RemarksPanel wsId={wsId} userId={userId} />
                </TabsContent>
                <TabsContent value="ai" className="m-0 space-y-4"><AITab data={data} onGenerate={generateAI} busy={aiBusy} /></TabsContent>
              </div>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProfileHeader({ id, initials }) {
  return (
    <div className="px-6 py-5 border-b flex items-start gap-4 bg-muted/10">
      <Avatar className="w-16 h-16 rounded-none">
        {id.avatar_data_url && <AvatarImage src={id.avatar_data_url} />}
        <AvatarFallback className="rounded-none bg-foreground text-background font-black text-xl">{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-xl font-bold truncate" data-testid="emp-profile-name">{id.name}</div>
        <div className="text-sm text-muted-foreground truncate">{[id.designation, id.department].filter(Boolean).join(" · ") || "—"}</div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          {id.employment_type && <Badge variant="outline" className="rounded-none text-[10px]">{id.employment_type.replace(/_/g, " ")}</Badge>}
          {id.status && <Badge variant="outline" className="rounded-none text-[10px]">{id.status}</Badge>}
          {id.joining_date && <span className="text-muted-foreground inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" />Joined {fmt(id.joining_date)} · {tenureLabel(id.tenure_days)}</span>}
          {id.manager && <span className="inline-flex items-center gap-1 text-muted-foreground"><User2 className="w-3 h-3" />Reports to <b className="text-foreground">{id.manager.name}</b></span>}
        </div>
      </div>
      {id.employee_id && (
        <Link to={`/app/employees/${id.employee_id}`} onClick={(e) => e.stopPropagation()}>
          <Button variant="outline" size="sm" className="rounded-none" data-testid="emp-profile-open-full">
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Full view
          </Button>
        </Link>
      )}
    </div>
  );
}

// ─────────────────── Overview tab ───────────────────

function OverviewTab({ data }) {
  const a = data.attendance.summary;
  const t = data.tasks;
  const p = data.points;
  const l = data.leaves;
  const w = data.wfh;
  const g = data.goals;
  const id = data.identity;

  const stats = [
    { l: "Attendance", v: `${a.attendance_rate_pct}%`, sub: `${a.present_days}/${a.workdays_in_range} workdays`, tone: "emerald" },
    { l: "Avg late", v: `${a.avg_late_minutes}m`, sub: `${a.late_incidence} events`, tone: a.late_incidence ? "amber" : "muted" },
    { l: "Leave (yr)", v: l.approved_days_this_year, sub: `${l.pending_count} pending`, tone: "sky" },
    { l: "WFH (30d)", v: w.approved_last_30d, sub: `${w.approved_last_90d} in ${data.window.days}d`, tone: "violet" },
    { l: "Open tasks", v: t.open_count, sub: t.overdue_count ? `${t.overdue_count} overdue` : "on schedule", tone: t.overdue_count ? "red" : "emerald" },
    { l: "Points", v: p.total, sub: p.rank ? `#${p.rank}` : "—", tone: "amber" },
    { l: "Active goals", v: g.count, sub: g.count ? "in progress" : "none", tone: "violet" },
    { l: "Anomalies", v: a.anomaly_count, sub: `${data.window.days}d`, tone: a.anomaly_count ? "orange" : "muted" },
  ];
  const toneCls = {
    emerald: "text-emerald-600", sky: "text-sky-600", violet: "text-violet-600",
    amber: "text-amber-600", red: "text-red-600", orange: "text-orange-600", muted: "text-foreground",
  };
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="emp-overview-stats">
        {stats.map((s) => (
          <div key={s.l} className="border p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.l}</div>
            <div className={`text-2xl font-mono font-black ${toneCls[s.tone] || ""}`}>{s.v}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <div className="border p-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Contact</div>
          {id.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> {id.email}</div>}
          {id.phone && <div className="flex items-center gap-2 mt-1"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> {id.phone}</div>}
          {id.work_email && id.work_email !== id.email && <div className="flex items-center gap-2 mt-1 text-muted-foreground"><Briefcase className="w-3.5 h-3.5" /> {id.work_email}</div>}
        </div>
        {data.ai?.content && (
          <div className="border border-violet-500/30 bg-violet-500/5 p-4" data-testid="emp-overview-ai">
            <div className="text-[11px] uppercase tracking-wider text-violet-600 mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI snapshot</div>
            <p className="text-sm line-clamp-4">{data.ai.content.summary}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────── Attendance tab ───────────────────

function AttendanceTab({ data }) {
  const rows = data.attendance.recent;
  const trend = useMemo(() => {
    const by = new Map();
    for (const r of rows) {
      const wm = r.work_minutes || 0;
      by.set(r.date, {
        date: r.date,
        hours: Math.round(wm / 60 * 10) / 10,
        late: r.late_minutes || 0,
      });
    }
    return Array.from(by.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);
  return (
    <div className="space-y-4">
      <div className="border p-4">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
          <Clock className="w-3 h-3" /> Hours & lateness — last {data.window.days} days
        </div>
        {trend.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No attendance records in this window.</div>
        ) : (
          <div className="h-56" data-testid="emp-att-chart">
            <ResponsiveContainer>
              <LineChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", fontSize: 11 }} />
                <Line yAxisId="l" type="monotone" dataKey="hours" stroke="#10b981" strokeWidth={2} dot={false} name="Hours worked" />
                <Line yAxisId="r" type="monotone" dataKey="late" stroke="#ef4444" strokeWidth={2} dot={false} name="Late (min)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="border" data-testid="emp-att-log">
        <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Raw check-in log ({rows.length})</div>
        {rows.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No records.</div>}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[720px]">
              <thead className="bg-muted/40 text-left text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5">Date</th>
                  <th className="px-3 py-1.5">Status</th>
                  <th className="px-3 py-1.5">In</th>
                  <th className="px-3 py-1.5">Out</th>
                  <th className="px-3 py-1.5">Hours</th>
                  <th className="px-3 py-1.5">Late</th>
                  <th className="px-3 py-1.5">Location</th>
                  <th className="px-3 py-1.5">Anomalies</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.date} className="border-t" data-testid={`emp-att-row-${r.date}`}>
                    <td className="px-3 py-1.5 font-mono">{r.date}</td>
                    <td className="px-3 py-1.5">{r.status || "—"}</td>
                    <td className="px-3 py-1.5 font-mono">{fmtTime(r.clock_in, data?.employee?.timezone)}</td>
                    <td className="px-3 py-1.5 font-mono">{fmtTime(r.clock_out, data?.employee?.timezone)}</td>
                    <td className="px-3 py-1.5 font-mono">{r.work_minutes ? (r.work_minutes / 60).toFixed(1) : "—"}</td>
                    <td className="px-3 py-1.5 font-mono">{r.late_minutes ? `+${r.late_minutes}m` : "—"}</td>
                    <td className="px-3 py-1.5 truncate max-w-40 text-muted-foreground">
                      {r.location ? <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{String(r.location).slice(0, 40)}</span> : "—"}
                    </td>
                    <td className="px-3 py-1.5">
                      {(r.anomalies || []).map((a) => (
                        <span key={a} className="inline-flex items-center gap-0.5 text-[10px] bg-orange-500/15 text-orange-600 px-1 py-0.5 mr-1">
                          <AlertTriangle className="w-2.5 h-2.5" />{a.replace(/_/g, " ")}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────── Leaves tab ───────────────────

function LeavesTab({ data }) {
  const l = data.leaves;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="emp-leave-balances">
        {(l.balances || []).map((b) => (
          <div key={b.type_id} className="border p-3" style={{ borderTopColor: b.color, borderTopWidth: 3 }}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{b.name}</div>
            <div className="text-2xl font-mono font-black">{b.available}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{b.used} used · {b.pending} pending / {b.accrued} accrued</div>
          </div>
        ))}
        {(l.balances || []).length === 0 && <div className="text-sm text-muted-foreground col-span-full">No leave types configured.</div>}
      </div>
      <div className="border" data-testid="emp-leave-list">
        <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Recent leave requests</div>
        {(l.recent || []).length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No requests.</div>}
        {(l.recent || []).map((r) => (
          <div key={r.id} className="border-t px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
            <Plane className="w-3 h-3 text-muted-foreground" />
            <Badge variant="outline" className="rounded-none text-[9px]">{r.type_code || r.type_name || "Leave"}</Badge>
            <span className="font-mono">{r.from_date}{r.to_date !== r.from_date ? `→${r.to_date}` : ""}{r.half_day ? " (½)" : ""}</span>
            <span className="text-muted-foreground flex-1 truncate">{r.reason}</span>
            <span className="font-mono font-bold">{r.days}d</span>
            <Badge variant="outline" className={`rounded-none text-[9px] ${r.status === "approved" ? "text-emerald-600" : r.status === "rejected" ? "text-red-500" : ""}`}>{r.status}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────── WFH tab ───────────────────

function WfhTab({ data }) {
  const w = data.wfh;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div className="border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last 30 days</div><div className="text-2xl font-mono font-black text-violet-600">{w.approved_last_30d}</div><div className="text-[10px] text-muted-foreground">approved WFH days</div></div>
        <div className="border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last {data.window.days} days</div><div className="text-2xl font-mono font-black text-violet-600">{w.approved_last_90d}</div><div className="text-[10px] text-muted-foreground">approved WFH days</div></div>
      </div>
      <div className="border" data-testid="emp-wfh-list">
        <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Recent WFH requests</div>
        {(w.recent || []).length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No requests.</div>}
        {(w.recent || []).map((r) => (
          <div key={r.id} className="border-t px-3 py-2 flex items-center gap-2 text-xs">
            <Home className="w-3 h-3 text-muted-foreground" />
            <span className="font-mono">{r.date}</span>
            <span className="text-muted-foreground">{r.week}</span>
            <span className="flex-1 truncate text-muted-foreground">{r.reason}</span>
            <Badge variant="outline" className={`rounded-none text-[9px] ${r.status === "approved" ? "text-emerald-600" : r.status === "rejected" ? "text-red-500" : ""}`}>{r.status}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────── Tasks tab ───────────────────

function TasksTab({ data }) {
  const t = data.tasks;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        <div className="border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Open</div><div className="text-2xl font-mono font-black">{t.open_count}</div></div>
        <div className="border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Overdue</div><div className={`text-2xl font-mono font-black ${t.overdue_count ? "text-red-600" : "text-emerald-600"}`}>{t.overdue_count}</div></div>
        <div className="border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Done ({data.window.days}d)</div><div className="text-2xl font-mono font-black text-emerald-600">{t.done_last_window}</div></div>
      </div>
      <div className="border" data-testid="emp-tasks-list">
        <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Open tasks (up to 10)</div>
        {(t.recent_open || []).length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No open tasks.</div>}
        {(t.recent_open || []).map((task) => (
          <div key={task.id} className="border-t px-3 py-2 flex items-center gap-2 text-xs">
            <ListChecks className="w-3 h-3 text-muted-foreground" />
            <span className="flex-1 truncate">{task.title}</span>
            {task.priority && <Badge variant="outline" className="rounded-none text-[9px]">{task.priority}</Badge>}
            <span className={`font-mono ${task.due_date && task.due_date < new Date().toISOString().slice(0, 10) ? "text-red-500" : "text-muted-foreground"}`}>{task.due_date || "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────── Goals tab ───────────────────

function GoalsTab({ data }) {
  const g = data.goals;
  return (
    <div className="border" data-testid="emp-goals-list">
      <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Active goals ({g.count})</div>
      {(g.active || []).length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No active goals.</div>}
      {(g.active || []).map((goal) => (
        <div key={goal.id} className="border-t px-3 py-2 flex items-center gap-2 text-xs">
          <Target className="w-3 h-3 text-muted-foreground" />
          <span className="flex-1 truncate">{goal.title}</span>
          {goal.horizon && <Badge variant="outline" className="rounded-none text-[9px]">{goal.horizon}</Badge>}
          {goal.mode && <Badge variant="outline" className="rounded-none text-[9px]">{goal.mode}</Badge>}
          <span className="font-mono text-[11px]">{Math.round(goal.progress_pct || 0)}%</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────── Points tab ───────────────────

function PointsTab({ data }) {
  const p = data.points;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <div className="border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total points</div><div className="text-2xl font-mono font-black text-amber-600">{p.total}</div></div>
        <div className="border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Rank</div><div className="text-2xl font-mono font-black">{p.rank ? `#${p.rank}` : "—"}</div></div>
      </div>
      <div className="border" data-testid="emp-points-list">
        <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Recent ledger entries</div>
        {(p.recent || []).length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No points activity.</div>}
        {(p.recent || []).map((row, i) => (
          <div key={i} className="border-t px-3 py-2 flex items-center gap-2 text-xs">
            <Trophy className="w-3 h-3 text-amber-500" />
            <span className={`font-mono font-bold ${row.points >= 0 ? "text-emerald-600" : "text-red-500"}`}>{row.points > 0 ? `+${row.points}` : row.points}</span>
            <span className="flex-1 truncate">{row.reason || "—"}</span>
            <span className="text-muted-foreground font-mono text-[10px]">{fmt(row.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────── AI tab ───────────────────

function AITab({ data, onGenerate, busy }) {
  const c = data.ai?.content;
  return (
    <div className="space-y-4" data-testid="emp-ai-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {data.ai ? `AI summary generated ${fmt(data.ai.created_at)}` : "No AI summary yet."}
        </div>
        <Button size="sm" variant="outline" className="rounded-none" onClick={onGenerate} disabled={busy} data-testid="emp-ai-generate">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          {busy ? "Generating…" : (c ? "Refresh" : "Generate")}
        </Button>
      </div>
      {c && (
        <div className="border border-violet-500/30 bg-violet-500/5 p-4 space-y-3">
          <p className="text-sm">{c.summary}</p>
          {[["strengths", "Strengths", "text-emerald-600"],
            ["risks", "Risks", "text-red-600"],
            ["talking_points", "1:1 talking points", "text-violet-600"]].map(([k, label, tone]) => (
            (c[k] || []).length > 0 && (
              <div key={k}>
                <p className={`text-[10px] uppercase tracking-wide ${tone}`}>{label}</p>
                <ul className="list-disc pl-5 text-sm mt-1 space-y-0.5">{c[k].map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}


// R268kkk · Feb 2026 — Employee timesheet subtab inside the profile
// popup. Reuses the timesheet-advanced backend endpoint with a fixed
// assignee filter (this employee's user_id) and lets the viewer slice
// by date range + clients + brands + projects (all searchable
// multi-selects with sorted options).
const _PRESETS = [
  { value: "this_month",  label: "This month" },
  { value: "last_month",  label: "Last month" },
  { value: "last30",      label: "Last 30 days" },
  { value: "last90",      label: "Last 90 days" },
  { value: "this_fy",     label: "This FY (Apr–Mar)" },
  { value: "lifetime",    label: "Lifetime" },
  { value: "custom",      label: "Custom range…" },
];

function TimesheetTab({ userId, wsId }) {
  const [preset, setPreset] = React.useState("last30");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [clients, setClients] = React.useState([]);
  const [brands, setBrands] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [clientF, setClientF] = React.useState([]);
  const [brandF, setBrandF] = React.useState([]);
  const [projectF, setProjectF] = React.useState([]);
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  // Fetch filter option lists once when the tab mounts.
  React.useEffect(() => {
    if (!wsId) return;
    (async () => {
      try {
        const [c, b, p] = await Promise.all([
          api.get(`/workspaces/${wsId}/clients?limit=500`).then((r) => r.data),
          api.get(`/workspaces/${wsId}/brands?limit=500`).then((r) => r.data),
          api.get(`/workspaces/${wsId}/projects?limit=1000`).then((r) => r.data),
        ]);
        const sortByName = (a, b_) => (a.name || "").localeCompare(b_.name || "");
        setClients((c.rows || c || []).sort(sortByName));
        setBrands((b.rows || b || []).sort(sortByName));
        setProjects((p.rows || p || []).sort(sortByName));
      } catch { /* ignore */ }
    })();
  }, [wsId]);

  const run = React.useCallback(async () => {
    if (!wsId || !userId) return;
    setBusy(true);
    try {
      const req = {
        preset,
        pivots: ["project"],
        bucket: "none",
        assignee_ids: [userId],
        client_ids: clientF,
        brand_ids: brandF,
        project_ids: projectF,
      };
      if (preset === "custom") { req.start_date = startDate; req.end_date = endDate; }
      const r = await api.post(`/workspaces/${wsId}/reports/timesheet-advanced`, req);
      setData(r.data);
    } catch (e) { toast.error("Failed to load timesheet"); }
    finally { setBusy(false); }
  }, [wsId, userId, preset, startDate, endDate, clientF, brandF, projectF]);

  React.useEffect(() => { run(); /* eslint-disable-next-line */ }, [userId, wsId]);

  const fmtH = (h) => (Number(h) || 0).toFixed(1) + "h";

  return (
    <div className="space-y-3" data-testid="emp-timesheet-tab">
      {/* Filters */}
      <div className="border border-border bg-muted/20 p-2 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Range</span>
          <select value={preset} onChange={(e) => setPreset(e.target.value)}
                  className="h-8 text-xs border border-border rounded-none px-2 bg-background"
                  data-testid="emp-ts-preset">
            {_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          {preset === "custom" && (
            <>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                     className="h-8 w-36 rounded-none text-xs" data-testid="emp-ts-start" />
              <span className="text-xs text-muted-foreground">→</span>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                     className="h-8 w-36 rounded-none text-xs" data-testid="emp-ts-end" />
            </>
          )}
          <Button size="sm" className="rounded-none h-8 ml-auto"
                  disabled={busy} onClick={run} data-testid="emp-ts-apply">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            Apply
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <MultiSelect placeholder="Clients"
                       options={clients.map((c) => ({ value: c.id, label: c.name }))}
                       value={clientF} onChange={setClientF}
                       data-testid="emp-ts-clients" />
          <MultiSelect placeholder="Brands"
                       options={brands.map((b) => ({ value: b.id, label: b.name }))}
                       value={brandF} onChange={setBrandF}
                       data-testid="emp-ts-brands" />
          <MultiSelect placeholder="Projects"
                       options={projects.map((p) => ({ value: p.id, label: p.name }))}
                       value={projectF} onChange={setProjectF}
                       data-testid="emp-ts-projects" />
        </div>
      </div>

      {/* Totals */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="emp-ts-totals">
          <div className="border border-border p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total hours</div>
            <div className="text-lg font-bold font-mono mt-0.5">{fmtH(data.tree?.hours_total)}</div>
            <div className="text-[10px] text-muted-foreground">{data.entry_count || 0} entries</div>
          </div>
          <div className="border border-border p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Billable</div>
            <div className="text-lg font-bold font-mono mt-0.5 text-emerald-700">{fmtH(data.tree?.hours_billable)}</div>
          </div>
          <div className="border border-border p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Non-billable</div>
            <div className="text-lg font-bold font-mono mt-0.5 text-amber-700">{fmtH(data.tree?.hours_non_billable)}</div>
          </div>
          <div className="border border-border p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Δ vs previous</div>
            <div className={"text-lg font-bold font-mono mt-0.5 " +
                 (data.compare?.delta_pct > 0 ? "text-emerald-700" :
                  data.compare?.delta_pct < 0 ? "text-rose-700" : "text-muted-foreground")}>
              {data.compare?.delta_pct === null || data.compare?.delta_pct === undefined ? "—"
                : (data.compare.delta_pct > 0 ? "+" : "") + data.compare.delta_pct + "%"}
            </div>
            <div className="text-[10px] text-muted-foreground">{fmtH(data.compare?.prev_total_hours)} prev</div>
          </div>
        </div>
      )}

      {/* Per-project breakdown */}
      {data && (data.tree?.children || []).length > 0 && (
        <div className="border border-border overflow-auto max-h-[52vh]" data-testid="emp-ts-project-grid">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-muted z-10">
              <tr>
                <th className="text-left px-3 py-1.5 border border-border font-semibold text-[10px] uppercase tracking-widest text-muted-foreground">Project</th>
                <th className="text-right px-2 py-1.5 border border-border font-semibold text-[10px] uppercase tracking-widest text-muted-foreground">Hours</th>
                <th className="text-right px-2 py-1.5 border border-border font-semibold text-[10px] uppercase tracking-widest text-muted-foreground">Billable</th>
                <th className="text-right px-2 py-1.5 border border-border font-semibold text-[10px] uppercase tracking-widest text-muted-foreground">Entries</th>
              </tr>
            </thead>
            <tbody>
              {data.tree.children.map((c) => (
                <tr key={c.id} className="hover:bg-primary/5" data-testid={`emp-ts-row-${c.id}`}>
                  <td className="px-3 py-1 border border-border/60 truncate" title={c.label}>{c.label}</td>
                  <td className="px-2 py-1 border border-border/60 text-right font-mono font-semibold">{fmtH(c.hours_total)}</td>
                  <td className="px-2 py-1 border border-border/60 text-right font-mono text-emerald-700">{fmtH(c.hours_billable)}</td>
                  <td className="px-2 py-1 border border-border/60 text-right font-mono text-muted-foreground">{c.entry_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && (data.tree?.children || []).length === 0 && (
        <div className="text-xs text-muted-foreground p-4 text-center border border-border">
          No time entries for this employee in the selected range.
        </div>
      )}
    </div>
  );
}
